import { QueryResult, useApolloClient } from '@apollo/client';
import { Query } from '@apollo/client/react/components';
import { Bar, Position, Size } from '@busybusy/webapp-react-ui';
import { EquipmentModelType } from '__generated__/graphql';
import classNames from 'classnames';
import BasicPicker, { IBasicPickerValue } from 'components/foundation/pickers/basic-picker/BasicPicker/BasicPicker';
import LazyLoadPicker from 'components/foundation/pickers/LazyLoadPicker/LazyLoadPicker';
import useEquipmentVersion2Enabled from 'hooks/feature/equipment/useEquipmentVersion2Enabled/useEquipmentVersion2Enabled';
import _ from 'lodash';
import { Component } from 'react';
import { ClassName } from 'types/ClassName';
import { IEquipmentType } from 'types/EquipmentType';
import { equipmentModelTypeLabelMap } from 'utils/constants/equipmentConstants';
import { equipmentTypeQuery, equipmentTypesQuery } from './equipment-type-picker-queries';

export interface IEquipmentTypePickerProps<T extends string> {
  value: T | null;
  onSelect: (equipmentType: T | null) => void;
  minWidth?: string; // has default
  className?: ClassName;
  placeholder?: string;
  error?: boolean;
  position?: Position;
  client?: any;
  fetchPolicy?: string;
  searchArchived?: boolean;
  autofocus?: boolean;
}

const options: Array<IBasicPickerValue<EquipmentModelType>> = [
  {
    id: EquipmentModelType.Equipment,
    name: equipmentModelTypeLabelMap[EquipmentModelType.Equipment],
  },

  {
    id: EquipmentModelType.Tool,
    name: equipmentModelTypeLabelMap[EquipmentModelType.Tool],
  },

  {
    id: EquipmentModelType.Attachment,
    name: equipmentModelTypeLabelMap[EquipmentModelType.Attachment],
  },
];

export default function ToggledPicker<T extends string>(props: IEquipmentTypePickerProps<T>) {
  const apolloClient = useApolloClient();
  const isEquipmentVersion2Enabled = useEquipmentVersion2Enabled();
  if (isEquipmentVersion2Enabled) {
    return <EquipmentTypePicker {...(props as unknown as IEquipmentTypePickerProps<EquipmentModelType>)} />;
  } else {
    return (
      <EquipmentTypeModelPicker {...(props as unknown as IEquipmentTypePickerProps<string>)} client={apolloClient} />
    );
  }
}

export function EquipmentTypePicker({
  minWidth = '350px',
  className,
  ...rest
}: IEquipmentTypePickerProps<EquipmentModelType>) {
  const classes = classNames('equipment-type-picker', className);
  return (
    <BasicPicker data-testid="equipment-type-picker" className={classes} data={options} minWidth={minWidth} {...rest} />
  );
}

interface IState {
  type: IEquipmentType[];
  loadedAll: boolean;
}

class EquipmentTypeModelPicker extends Component<IEquipmentTypePickerProps<string>, IState> {
  public static defaultProps: any;
  public state: IState = {
    loadedAll: false,
    type: [],
  };

  // Fetch a page of equipment types
  public fetchEquipmentTypes = (after?: string): Promise<IEquipmentType[]> => {
    return new Promise((resolve, reject) => {
      this.props.client
        .query({
          fetchPolicy: this.props.fetchPolicy ?? 'cache-first',
          query: equipmentTypesQuery,
          variables: {
            after,
            filter: {
              display: { equal: true },
              deletedOn: { isNull: true },
            },
            first: 10,
          },
        })
        .then((result: { data: { equipmentTypes: IEquipmentType[] } }) => {
          resolve(result.data.equipmentTypes);
        })
        .catch(() => {
          reject();
        });
    });
  };

  // Handle new results from the server
  public handleDidLoad = (type: IEquipmentType[], error: boolean, loadedAll: boolean) => {
    if (!error) {
      this.setState({
        loadedAll,
        type,
      });
    }
  };

  // Render the picker's value
  public renderValueTemplate = () => {
    const value = this.props.value;

    if (value) {
      return (
        <Query query={equipmentTypeQuery} variables={{ typeId: value }}>
          {({ data }: QueryResult) => {
            return <>{_.get(data, 'equipmentTypes[0][title]')}</>;
          }}
        </Query>
      );
    }
    return <></>;
  };

  // Render a row withing the picker
  public renderRow = (type: IEquipmentType) => (
    <Bar size={Size.SMALL} className="px-3">
      <div className="ellipsis">{type.title}</div>
    </Bar>
  );

  // Handle when a value is selected from the list
  public handleSelect = (type: IEquipmentType | null) => {
    this.props.onSelect(type ? type.id : null);
  };

  // Handle the picker closing
  public handleClose = () => {
    this.setState({
      loadedAll: false,
      type: [],
    });
  };

  // Render
  public render() {
    const { className, placeholder, minWidth = '350px', position, value, autofocus } = this.props;

    const classes = classNames(
      {
        'equipment-type-picker': true,
      },
      className
    );

    return (
      <LazyLoadPicker
        data-testid={'equipment-type-picker'}
        value={value}
        getData={this.fetchEquipmentTypes}
        didLoad={this.handleDidLoad}
        data={this.state.type}
        loadedAll={this.state.loadedAll}
        onSelect={this.handleSelect}
        onClose={this.handleClose}
        valueTemplate={this.renderValueTemplate()}
        renderRow={this.renderRow}
        minWidth={minWidth}
        position={position}
        placeholder={placeholder}
        autofocus={autofocus}
        className={classes}
      />
    );
  }
}
